import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Heart, Reply, Coins, Send, MessageCircle } from "lucide-react";
import { listComments, createComment, likeComment, tipComment, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { COMMENTS } from "@/constants/testIds";

export default function CommentsSection({ slug }) {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ comments: [], total: 0 });
  const [body, setBody] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [likedIds, setLikedIds] = useState({}); // local optimistic

  const load = () => listComments(slug).then(setData);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!user) { nav("/login"); return; }
    if (!body.trim()) return;
    setBusy(true);
    try {
      await createComment(slug, body.trim(), replyTo);
      setBody(""); setReplyTo(null);
      await load();
      toast.success("Comment posted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doLike = async (c) => {
    if (!user) { nav("/login"); return; }
    try {
      const r = await likeComment(c.id);
      setLikedIds((m) => ({ ...m, [c.id]: r.liked }));
      c.likes = (c.likes || 0) + (r.liked ? 1 : -1);
      setData({ ...data });
    } catch {}
  };

  const doTip = async (c) => {
    if (!user) { nav("/login"); return; }
    const s = window.prompt(`Tip coins to ${c.user_name}`, "10");
    const n = Number(s);
    if (!n || n <= 0) return;
    try {
      await tipComment(c.id, n);
      c.tips_received = (c.tips_received || 0) + n;
      setData({ ...data });
      await refresh();
      toast.success(`Sent ${n} coins to ${c.user_name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <section data-testid={COMMENTS.root} className="mt-12">
      <div className="flex items-center gap-2 mb-5">
        <MessageCircle className="w-5 h-5 text-[#00F0FF]" />
        <h2 className="font-heading font-black text-2xl uppercase tracking-tight">Comments · {data.total}</h2>
      </div>

      {/* Composer */}
      <form onSubmit={submit} className="glass rounded-2xl p-4 mb-6">
        <div className="flex gap-3 items-start">
          <Avatar user={user} />
          <div className="flex-1">
            {replyTo && (
              <div className="mono text-[10px] text-[#00F0FF] mb-1.5 flex items-center gap-1">
                Replying · <button type="button" onClick={() => setReplyTo(null)} className="underline">cancel</button>
              </div>
            )}
            <textarea data-testid={COMMENTS.input} value={body} onChange={(e) => setBody(e.target.value)}
              placeholder={user ? "Drop a comment…" : "Sign in to comment"} rows={2}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg p-3 text-sm focus:outline-none focus:border-[#00F0FF]/60 resize-none" />
            <div className="flex justify-end mt-2">
              <button data-testid={COMMENTS.submit} disabled={busy || !body.trim()}
                className="inline-flex items-center gap-1.5 bg-[#00F0FF] hover:bg-white text-black font-bold mono text-[10px] uppercase tracking-widest px-4 py-2 rounded-full disabled:opacity-40 transition-colors">
                <Send className="w-3 h-3" /> Post
              </button>
            </div>
          </div>
        </div>
      </form>

      {/* Threads */}
      {data.comments.length === 0 && (
        <div className="mono text-xs text-white/40 py-6 text-center">// no comments yet. be first.</div>
      )}
      <div className="space-y-4">
        {data.comments.map((c) => (
          <CommentItem key={c.id} c={c} onLike={doLike} onTip={doTip} onReply={setReplyTo}>
            {c.replies?.map((r) => (
              <div key={r.id} className="ml-10 mt-3">
                <CommentItem c={r} onLike={doLike} onTip={doTip} onReply={setReplyTo} nested />
              </div>
            ))}
          </CommentItem>
        ))}
      </div>
    </section>
  );
}

function Avatar({ user, comment }) {
  const color = comment?.user_avatar || user?.avatar_color || "#00F0FF";
  const letter = ((comment?.user_name || user?.name || user?.email || "?") + "")[0].toUpperCase();
  return (
    <div className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-heading font-black"
      style={{ background: `${color}22`, color, border: `1px solid ${color}55` }}>
      {letter}
    </div>
  );
}

function CommentItem({ c, onLike, onTip, onReply, children, nested }) {
  return (
    <div data-testid={COMMENTS.item} className={`glass rounded-2xl p-4 ${nested ? "border-l-2 border-[#00F0FF]/20" : ""}`}>
      <div className="flex items-start gap-3">
        <Avatar comment={c} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <div className="font-bold text-sm">{c.user_name}</div>
            <div className="mono text-[10px] text-white/40">{new Date(c.created_at).toLocaleDateString()}</div>
          </div>
          <p className="text-white/85 text-sm mt-1 whitespace-pre-wrap">{c.body}</p>
          <div className="flex items-center gap-3 mt-2 mono text-[10px] text-white/60">
            <button data-testid={COMMENTS.like} onClick={() => onLike(c)} className="flex items-center gap-1 hover:text-[#FF003C]">
              <Heart className="w-3 h-3" /> {c.likes || 0}
            </button>
            {!nested && (
              <button data-testid={COMMENTS.reply} onClick={() => onReply(c.id)} className="flex items-center gap-1 hover:text-[#00F0FF]">
                <Reply className="w-3 h-3" /> Reply
              </button>
            )}
            <button data-testid={COMMENTS.tip} onClick={() => onTip(c)} className="flex items-center gap-1 hover:text-[#FFE300]">
              <Coins className="w-3 h-3" /> Tip {c.tips_received ? `· ${c.tips_received}` : ""}
            </button>
          </div>
        </div>
      </div>
      {children}
    </div>
  );
}
